import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Scanner } from "@yudiel/react-qr-scanner";
import { 
  Wine, LogOut, Search, CheckCircle, XCircle, AlertTriangle, 
  Minus, RefreshCw, User, Camera, Keyboard
} from "lucide-react";
import { API } from "@/config/api";
import LaserBackground from "@/components/common/LaserBackground";
import PasswordInput from "@/components/common/PasswordInput";

const BarraPanel = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [credentials, setCredentials] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Scanner mode
  const [modoEscaner, setModoEscaner] = useState(false);
  const [escanerError, setEscanerError] = useState("");
  
  // Bonocopa search
  const [codigoInput, setCodigoInput] = useState("");
  const [bonocopaActual, setBonocopaActual] = useState(null);
  const [evento, setEvento] = useState(null);
  const [searchError, setSearchError] = useState("");
  const [searching, setSearching] = useState(false);
  const [consumiendo, setConsumiendo] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  
  // History of recent scans
  const [historial, setHistorial] = useState([]);
  
  const inputRef = useRef(null);

  // Auto-focus on input
  useEffect(() => {
    if (isLoggedIn && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoggedIn, bonocopaActual]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const creds = btoa(`${username}:${password}`);
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password
      });

      if (response.data.success) {
        setCredentials(creds);
        setIsLoggedIn(true);
        localStorage.setItem("barra_credentials", creds);
        localStorage.setItem("barra_username", username);
      }
    } catch (err) {
      setError("Credenciales incorrectas");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setCredentials("");
    localStorage.removeItem("barra_credentials");
    localStorage.removeItem("barra_username");
  };

  // Check saved credentials on mount
  useEffect(() => {
    const savedCreds = localStorage.getItem("barra_credentials");
    const savedUsername = localStorage.getItem("barra_username");
    if (savedCreds && savedUsername) {
      setCredentials(savedCreds);
      setUsername(savedUsername);
      setIsLoggedIn(true);
    }
  }, []);

  const buscarBonocopa = async (codigo) => {
    if (!codigo || codigo.length < 3) return;
    
    setSearching(true);
    setSearchError("");
    setBonocopaActual(null);
    setEvento(null);
    setSuccessMessage("");

    try {
      const response = await axios.get(`${API}/bonocopas/verificar/${codigo.toUpperCase()}`);
      
      if (response.data.valid) {
        setBonocopaActual(response.data.bonocopa);
        
        // Get event info
        if (response.data.bonocopa.event_id) {
          try {
            const eventRes = await axios.get(`${API}/events/${response.data.bonocopa.event_id}`);
            setEvento(eventRes.data);
          } catch (e) {
            // Event not found, continue
          }
        }
      } else {
        setSearchError(response.data.message || "Bonocopa no válido");
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setSearchError("Código no encontrado");
      } else {
        setSearchError("Error al buscar el bonocopa");
      }
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    buscarBonocopa(codigoInput);
  };

  // Handle QR scan result
  const handleScan = (result) => {
    if (result && result[0]?.rawValue) {
      const codigo = result[0].rawValue.toUpperCase();
      setModoEscaner(false);
      setCodigoInput(codigo);
      buscarBonocopa(codigo);
    }
  };

  const handleScanError = (error) => {
    console.error("Scanner error:", error);
    setEscanerError("Error al acceder a la cámara. Verifica los permisos.");
  };

  const consumirCopa = async () => {
    if (!bonocopaActual) return;
    
    setConsumiendo(true);
    setSearchError("");

    try {
      const response = await axios.post(
        `${API}/admin/bonocopas/${bonocopaActual.id}/consumir`,
        {},
        { headers: { Authorization: `Basic ${credentials}` } }
      );

      if (response.data.success) {
        // Update local state
        setBonocopaActual(prev => ({
          ...prev,
          copas_consumidas: prev.copas_consumidas + 1
        }));
        
        setSuccessMessage(`✅ Copa servida. Quedan ${response.data.copas_restantes} copas.`);
        
        // Add to history
        setHistorial(prev => [{
          codigo: bonocopaActual.codigo,
          nombre: bonocopaActual.nombre,
          copas_restantes: response.data.copas_restantes,
          timestamp: new Date().toLocaleTimeString()
        }, ...prev.slice(0, 9)]);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
        
        // If no more cups, clear the bonocopa
        if (response.data.copas_restantes === 0) {
          setTimeout(() => {
            setBonocopaActual(null);
            setCodigoInput("");
            if (inputRef.current) inputRef.current.focus();
          }, 2000);
        }
      }
    } catch (err) {
      setSearchError(err.response?.data?.detail || "Error al consumir copa");
    } finally {
      setConsumiendo(false);
    }
  };

  const limpiarBusqueda = () => {
    setBonocopaActual(null);
    setEvento(null);
    setCodigoInput("");
    setSearchError("");
    setSuccessMessage("");
    setEscanerError("");
    if (!modoEscaner && inputRef.current) inputRef.current.focus();
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="hero-bg min-h-screen flex items-center justify-center">
        <LaserBackground />
        <div className="form-section max-w-md w-full mx-4 relative z-10">
          <div className="text-center mb-6">
            <Wine size={48} className="text-yellow-500 mx-auto mb-2" />
            <h1 className="text-2xl font-bold text-white">Panel de Barra</h1>
            <p className="text-[var(--text-muted)] text-sm">Control de Bonocopas</p>
          </div>

          {error && (
            <div className="error-message mb-4">{error}</div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="form-label">Usuario</label>
              <input
                type="text"
                className="custom-input"
                placeholder="Usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                data-testid="barra-login-username"
              />
            </div>
            <div>
              <label className="form-label">Contraseña</label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Contraseña"
                required
                testId="barra-login-password"
              />
            </div>
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="barra-login-btn"
            >
              {loading ? <span className="spinner"></span> : "Entrar"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Main Barra Panel
  return (
    <div className="hero-bg min-h-screen">
      <LaserBackground />
      <div className="main-container relative z-10 py-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <Wine size={32} className="text-yellow-500" />
            <div>
              <h1 className="text-xl font-bold text-white">Panel de Barra</h1>
              <p className="text-[var(--text-muted)] text-xs">Usuario: {username}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="btn-secondary flex items-center gap-2"
          >
            <LogOut size={18} />
            Salir
          </button>
        </div>

        {/* Mode Toggle - Escáner / Manual */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => {
              setModoEscaner(false);
              setEscanerError("");
            }}
            className={`flex-1 py-3 flex items-center justify-center gap-2 transition-all ${
              !modoEscaner
                ? 'bg-yellow-500 text-black font-bold'
                : 'bg-[var(--surface)] text-[var(--text-muted)] hover:text-white border border-[var(--border-color)]'
            }`}
          >
            <Keyboard size={20} />
            Manual
          </button>
          <button
            onClick={() => {
              setModoEscaner(true);
              setEscanerError("");
              setBonocopaActual(null);
            }}
            className={`flex-1 py-3 flex items-center justify-center gap-2 transition-all ${
              modoEscaner
                ? 'bg-yellow-500 text-black font-bold'
                : 'bg-[var(--surface)] text-[var(--text-muted)] hover:text-white border border-[var(--border-color)]'
            }`}
          >
            <Camera size={20} />
            Escáner QR
          </button>
        </div>

        {/* QR Scanner */}
        {modoEscaner && !bonocopaActual && (
          <div className="form-section mb-6">
            <div className="bg-black rounded-lg overflow-hidden mb-4" style={{ maxWidth: '400px', margin: '0 auto' }}>
              <Scanner
                onScan={handleScan}
                onError={handleScanError}
                constraints={{
                  facingMode: 'environment'
                }}
                styles={{
                  container: { width: '100%', paddingTop: '100%', position: 'relative' },
                  video: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }
                }}
              />
            </div>
            <p className="text-[var(--text-muted)] text-center text-sm">
              📷 Apunta la cámara al código QR del bonocopa
            </p>
            {escanerError && (
              <p className="text-red-400 text-center text-sm mt-2">{escanerError}</p>
            )}
          </div>
        )}

        {/* Manual Search Form */}
        {!modoEscaner && !bonocopaActual && (
          <div className="form-section mb-6">
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="flex-1 relative">
                <input
                  ref={inputRef}
                  type="text"
                  className="custom-input text-xl font-mono tracking-wider uppercase"
                  placeholder="BC-XXXXXXXX"
                  value={codigoInput}
                  onChange={(e) => setCodigoInput(e.target.value.toUpperCase())}
                  data-testid="bonocopa-search-input"
                />
                {codigoInput && (
                  <button
                    type="button"
                    onClick={limpiarBusqueda}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-white"
                  >
                    <XCircle size={20} />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="btn-primary px-6"
                disabled={searching || !codigoInput}
                data-testid="bonocopa-search-btn"
              >
                {searching ? <span className="spinner"></span> : <Search size={20} />}
              </button>
            </form>
          </div>
        )}

        {/* Error Message */}
        {searchError && (
          <div className="bg-red-500/20 border border-red-500 p-4 mb-6 flex items-center gap-3">
            <XCircle size={24} className="text-red-500" />
            <span className="text-red-400">{searchError}</span>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-500/20 border border-green-500 p-4 mb-6 flex items-center gap-3 animate-pulse">
            <CheckCircle size={24} className="text-green-500" />
            <span className="text-green-400 font-bold">{successMessage}</span>
          </div>
        )}

        {/* Bonocopa Card */}
        {bonocopaActual && (
          <div className="form-section border-2 border-yellow-500 mb-6">
            <div className="text-center mb-4">
              <p className="text-yellow-500 font-mono text-2xl font-bold tracking-wider">
                {bonocopaActual.codigo}
              </p>
              {bonocopaActual.tipo_nombre && (
                <p className="text-[var(--text-muted)] text-sm mt-1">{bonocopaActual.tipo_nombre}</p>
              )}
            </div>

            {/* Client Info */}
            <div className="bg-[var(--surface)] p-3 mb-4 flex items-center gap-3">
              <User size={20} className="text-[var(--text-muted)]" />
              <div>
                <p className="text-white font-medium">{bonocopaActual.nombre}</p>
                <p className="text-[var(--text-muted)] text-xs">{bonocopaActual.telefono}</p>
              </div>
            </div>

            {/* Event Info */}
            {evento && (
              <div className="bg-[var(--surface)] p-3 mb-4 text-center">
                <p className="text-[var(--primary)] font-bold">{evento.nombre}</p>
                <p className="text-[var(--text-muted)] text-xs">{evento.fecha} - {evento.sala}</p>
              </div>
            )}

            {/* Cups Display */}
            <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border border-yellow-500/50 p-6 text-center mb-4">
              <p className="text-[var(--text-muted)] text-sm mb-2">COPAS DISPONIBLES</p>
              <div className="flex items-center justify-center gap-4">
                <span className="text-6xl font-black text-yellow-500">
                  {bonocopaActual.copas_totales - bonocopaActual.copas_consumidas}
                </span>
                <span className="text-2xl text-[var(--text-muted)]">
                  / {bonocopaActual.copas_totales}
                </span>
              </div>
              <p className="text-[var(--text-muted)] text-xs mt-2">
                {bonocopaActual.copas_consumidas} copa(s) ya servida(s)
              </p>
            </div>

            {/* Consume Button */}
            {(bonocopaActual.copas_totales - bonocopaActual.copas_consumidas) > 0 ? (
              <button
                onClick={consumirCopa}
                disabled={consumiendo}
                className="w-full py-6 bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-xl uppercase tracking-wider transition-all flex items-center justify-center gap-3"
                data-testid="consumir-copa-btn"
              >
                {consumiendo ? (
                  <span className="spinner"></span>
                ) : (
                  <>
                    <Minus size={28} />
                    SERVIR 1 COPA
                  </>
                )}
              </button>
            ) : (
              <div className="w-full py-6 bg-gray-800 text-gray-500 font-bold text-xl uppercase text-center">
                🍸 BONOCOPA AGOTADO 🍸
              </div>
            )}

            {/* Warning */}
            <p className="text-red-400 text-xs text-center mt-3">
              ⚠️ No incluye bebidas premium ni champagne
            </p>

            {/* New Search Button */}
            <button
              onClick={limpiarBusqueda}
              className="w-full mt-4 py-3 border border-[var(--border-color)] text-[var(--text-muted)] hover:text-white hover:border-[var(--primary)] transition-all flex items-center justify-center gap-2"
            >
              <RefreshCw size={18} />
              Nueva Búsqueda
            </button>
          </div>
        )}

        {/* Recent History */}
        {historial.length > 0 && (
          <div className="form-section">
            <h3 className="text-[var(--text-muted)] text-sm uppercase tracking-wider mb-3">
              Historial Reciente
            </h3>
            <div className="space-y-2">
              {historial.map((item, idx) => (
                <div key={idx} className="bg-[var(--surface)] p-2 flex justify-between items-center text-sm">
                  <div>
                    <span className="text-yellow-500 font-mono">{item.codigo}</span>
                    <span className="text-[var(--text-muted)] ml-2">- {item.nombre}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-white">{item.copas_restantes} restantes</span>
                    <span className="text-[var(--text-muted)] text-xs">{item.timestamp}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BarraPanel;
